import React from 'react'
import { useEffect, useState, useContext } from 'react'
import { getForAllLocales, deleteSingleType, updateType } from './../services/Node/index'
import { toast } from 'react-toastify'
import SubTable from './SubTable'
import { Button } from 'react-bootstrap'
import DataContext from '../store/data-context'
import ConfirmModal from './ConfirmModal'
import Loader from './Loader'

function TypeTable() {
    const { type, tableData, setTableData, locales } = useContext(DataContext)
    const [selectedIndexWalk, setSelectedIndexWalk] = useState({})
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)

    useEffect(() => {
        setIsLoaded(false)
        if (type.value.length === 0)
            return
        const fetchTableData = async () => {
            const resp = await getForAllLocales(type.value)
            if (resp.success)
                setTableData(resp.data)
            else
                throw new Error(resp.error)
        }

        const fetchData = async () => {
            try {
                await fetchTableData()
                setIsLoaded(true)
            } catch (error) {
                toast.error('Error while fetching data. Reloading the page', {
                    onClose: () => window.location.reload(),
                    pauseOnHover: false
                })
            }
        }
        setSelectedIndexWalk({})
        fetchData()
    }, [type, setTableData])

    // Function to fetch the respective node,its current table and its parrent using walk where walk is an list of indexes to be followed in order to reach that node
    const getEntryRecursive = (indexWalk, updatedTableData, parent) => {
        const currentIndex = indexWalk.shift()
        if (indexWalk.length > 0)
            return getEntryRecursive(indexWalk, updatedTableData[currentIndex].children, updatedTableData[currentIndex])
        return { entry: updatedTableData[currentIndex], table: updatedTableData, parent: (parent ? parent : updatedTableData) }
    }

    const deleteHandler = async (indexWalk, idWalk) => {
        try {
            if (type.value === '')
                throw new Error('Please select a type')
            const updatedTableData = [...tableData]
            const index = indexWalk[indexWalk.length - 1]
            const { table, parent } = getEntryRecursive([...indexWalk], updatedTableData)
            const resp = await deleteSingleType({ idWalk: idWalk.slice(1), hasChildren: table.length > 1 }, idWalk[0], type.value)
            if (resp.success) {
                toast.success(`${type.value} entry deleted successfully`)
                table.splice(index, 1)
                if (table.length === 0) {
                    parent.hasChildren = false
                    parent.isExpanded = false
                }
                setTableData(updatedTableData)
            }
            else
                throw new Error(resp.error)
        } catch (error) {
            toast.error(error.message)
        }
    }

    const multiDeleteHandler = async () => {
        if (type.value === '')
            throw new Error('Please select a type')
        const updatedTableData = [...tableData]
        const remanainingSelectedIndexWalk = {}
        // Getting all the entries references from the tree
        const entries = Object.keys(selectedIndexWalk).map(key => {
            const { indexWalk, idWalk } = selectedIndexWalk[key]
            const { table, parent } = getEntryRecursive([...indexWalk], updatedTableData)
            return { table, parent, idWalk, key, indexWalk }
        })
        // Making a list of promises to delete all the individual selected types
        const deleteRequests = entries.map(({ table, idWalk }) => {
            const copyIdWalk = [...idWalk]
            return deleteSingleType({ idWalk: copyIdWalk.slice(1), hasChildren: table.length > 1 }, copyIdWalk[0], type.value)
        })
        // Waiting for all the requests to be completed
        const result = await Promise.all(deleteRequests)
        // Deleting the types whose requests were completed and saving the unresolved ones
        result.forEach((resp, index) => {
            try {
                const { table, idWalk, key, indexWalk, parent } = entries[index]
                if (!resp.success) {
                    remanainingSelectedIndexWalk[key] = { idWalk, indexWalk }
                    throw new Error(resp.error)
                }
                else {
                    table.splice(table.findIndex(x => x._id === idWalk[idWalk.length - 1]), 1)
                    if (table.length === 0) {
                        parent.hasChildren = false
                        parent.isExpanded = false
                    }
                }
            } catch (error) {
                toast.error(error.message)
            }
        })
        setTableData(updatedTableData)
        // resetting the selectedIndexWalk in case all requests got resolved else setting it to the unresolved requests
        setSelectedIndexWalk(remanainingSelectedIndexWalk)
    }

    const saveHandler = async (indexWalk, idWalk) => {
        try {
            if (type.value === '')
                throw new Error('Please select a type')
            const updatedTableData = [...tableData]
            const { entry } = getEntryRecursive([...indexWalk], updatedTableData)
            const resp = await updateType({ updatedVal: entry.updatedVal, idWalk: idWalk.slice(1) }, idWalk[0], type.value)
            if (resp.success) {
                toast.success(`${type.value} entry updated successfully`)
                entry.editable = !entry.editable
                entry.data.forEach(item => {
                    if (entry.updatedVal[item._id])
                        item.name = entry.updatedVal[item._id]
                })
                setTableData(updatedTableData)
            }
            else
                throw new Error(resp.error)
        } catch (error) {
            toast.error(error.message)
        }
    }

    const editHandler = (indexWalk) => {
        const updatedTableData = [...tableData]
        const { entry } = getEntryRecursive([...indexWalk], updatedTableData)
        entry.editable = !entry.editable
        entry.updatedVal = undefined
        setTableData(updatedTableData)
    }

    const editChangeHandler = (indexWalk, e, dataId) => {
        const updatedTableData = [...tableData]
        const { entry } = getEntryRecursive([...indexWalk], updatedTableData)
        if (!entry.updatedVal)
            entry.updatedVal = {}
        entry.updatedVal[dataId] = e.target.value.trimStart()
        setTableData(updatedTableData)
    }

    const expandHandler = (indexWalk) => {
        const updatedTableData = [...tableData]
        const { entry } = getEntryRecursive([...indexWalk], updatedTableData)
        entry.isExpanded = !entry.isExpanded
        setTableData(updatedTableData)
    }

    const checkHandler = (indexWalk, idWalk) => {
        const updatedTableData = [...tableData]
        const { entry } = getEntryRecursive([...indexWalk], updatedTableData)
        if (entry.isSelected)
            setSelectedIndexWalk(prevSelectedIndexWalk => {
                const { [entry.id]: deletedRow, ...remainingRows } = prevSelectedIndexWalk
                return remainingRows
            })
        else
            setSelectedIndexWalk(prevSelectedIndexWalk => {
                const isChildren = []
                const isNotChildren = []
                Object.keys(prevSelectedIndexWalk).forEach(key => key.includes(`${entry.id}c`) ? isChildren.push(key) : isNotChildren.push(key))
                // Filtering out the children whose parent got selected
                const remaingRows = isNotChildren.reduce((obj, key) => ({
                    ...obj,
                    [key]: prevSelectedIndexWalk[key]
                }), {})
                // Unselecting the children whose parent got selected
                isChildren.forEach((key) => {
                    const { entry } = getEntryRecursive([...prevSelectedIndexWalk[key].indexWalk], updatedTableData)
                    if (entry.isSelected)
                        entry.isSelected = false
                })
                return { ...remaingRows, [entry.id]: { indexWalk: [...indexWalk], idWalk: [...idWalk] } }
            })
        entry.isSelected = !entry.isSelected
        setTableData(updatedTableData)
    }

    const SubTableProps = { locales, expandHandler, editChangeHandler, editHandler, deleteHandler, saveHandler, setTableData, getEntryRecursive, topTableData: tableData, type, checkHandler, setShowConfirmModal }

    return (
        isLoaded
            ? (<>
                < ConfirmModal showConfirmModal={showConfirmModal} modalTitle="Confirmation" modalBody="Are you sure you want to delete the selected items?" closeConfirmModalHandler={() => setShowConfirmModal(false)
                } confirmHandler={multiDeleteHandler} />
                {
                    Object.keys(selectedIndexWalk).length > 0 &&
                    <div className='fixed-bottom'>
                        <Button variant='danger' onClick={() => setShowConfirmModal(true)}>Delete Selected</Button>
                    </div>
                }
                < SubTable SubTableProps={{ ...SubTableProps, tableData: tableData, indexWalk: [], idWalk: [] }} />
            </>)
            : <Loader />
    )
}

export default TypeTable